import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  updateAccount,
  updatePassword,
  updateKyc,
  submitKyc,
  fetchKyc,
  upgradeToCorporate,
} from "../../services/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export default function AccountSettings() {
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("account");
  const [loading, setLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [kycLoading, setKycLoading] = useState(false);
  const [isCorporateModalOpen, setIsCorporateModalOpen] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [files, setFiles] = useState({
    utility_bill: null,
    cac_certificate: null,
    board_letter: null,
  });

  const [form, setForm] = useState({
    first_name: user?.first_name || "Ayodeji",
    last_name: user?.last_name || "Bamisebi",
    email: user?.email || "tolubammy04@gmail.com",
    phone: user?.phone_number || "08121592978",
    hospital_number: user?.kyc?.hospital_number || "",
    next_of_kin_name: user?.kyc?.next_of_kin_name || "",
    next_of_kin_phone: user?.kyc?.next_of_kin_phone || "",
    next_of_kin_address: user?.kyc?.next_of_kin_address || "",
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [kycForm, setKycForm] = useState({
    account_type: "Individual",
    dob: "",
    address: "",
    next_of_kin_name: "",
    next_of_kin_phone: "",
    next_of_kin_address: "",
    id_number: "",
    id_type: "NIN",
  });

  const hasFormChanges = () => {
    return (
      form.first_name !== (user?.first_name || "Ayodeji") ||
      form.last_name !== (user?.last_name || "Bamisebi") ||
      form.phone !== (user?.phone_number || "08121592978") ||
      form.hospital_number !== (user?.kyc?.hospital_number || "") ||
      form.next_of_kin_name !== (user?.kyc?.next_of_kin_name || "") ||
      form.next_of_kin_phone !== (user?.kyc?.next_of_kin_phone || "") ||
      form.next_of_kin_address !== (user?.kyc?.next_of_kin_address || "")
    );
  };

  useEffect(() => {
    const loadKycData = async () => {
      try {
        const data = await fetchKyc();
        setKycData(data);
        setKycForm((prev) => ({
          ...prev,
          account_type: data?.account_type || "Individual",
          dob: data?.dob || "",
          address: data?.address || "",
          next_of_kin_name: data?.next_of_kin_name || "",
          next_of_kin_phone: data?.next_of_kin_phone || "",
          next_of_kin_address: data?.next_of_kin_address || "",
          id_number: data?.id_number || "",
          id_type: data?.id_type || "NIN",
        }));
      } catch (error) {
        console.log("Failed to load KYC details.");
      }
    };
    loadKycData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleKycChange = (e) => {
    const { name, value } = e.target;
    setKycForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles[0]) {
      setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
    }
  };

  const { updateUser } = useAuth();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await updateAccount({
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone,
        hospital_number: form.hospital_number,
        next_of_kin_name: form.next_of_kin_name,
        next_of_kin_phone: form.next_of_kin_phone,
        next_of_kin_address: form.next_of_kin_address,
      });
      // Update local context so hospital_number auto-fills immediately
      updateUser({
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone,
        kyc: { ...(user?.kyc || {}), hospital_number: form.hospital_number },
      });
      alert("Profile updated successfully! Your Hospital Number will now auto-fill on payments.");
    } catch (error) {
      alert("Something went wrong updating your profile.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (form.newPassword !== form.confirmNewPassword) {
      alert("New passwords do not match!");
      return;
    }
    if (form.newPassword.length < 8) {
      alert("New password must be at least 8 characters long!");
      return;
    }
    setLoading(true);
    try {
      await updatePassword({
        old_password: form.currentPassword,
        new_password: form.newPassword,
      });
      alert("Password updated successfully!");
      setForm((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      }));
    } catch (error) {
      alert("Failed to update password. Please check your current password.");
    } finally {
      setLoading(false);
    }
  };

  const handleKycSubmit = async () => {
    // Validation omitted for brevity
    setKycLoading(true);
    try {
      const kycPayload = { ...kycForm };
      if (kycData) {
        await updateKyc(kycPayload);
        alert("KYC details updated successfully!");
      } else {
        await submitKyc(kycPayload);
        alert("KYC details submitted successfully!");
      }
      const updatedKyc = await fetchKyc();
      setKycData(updatedKyc);
    } catch (error) {
      alert("Failed to submit/update KYC.");
    } finally {
      setKycLoading(false);
    }
  };

  const handleCorporateSubmit = async () => {
    if (!files.utility_bill || !files.cac_certificate || !files.board_letter) {
      alert("Please upload all required documents!");
      return;
    }
    setUpgradeLoading(true);
    try {
      const formData = new FormData();
      formData.append("utility_bill", files.utility_bill);
      formData.append("cac_certificate", files.cac_certificate);
      formData.append("board_letter", files.board_letter);
      await upgradeToCorporate(formData);
      alert("Account successfully upgraded to corporate!");
      setFiles({ utility_bill: null, cac_certificate: null, board_letter: null });
      setIsCorporateModalOpen(false);
    } catch (error) {
      alert("Failed to upgrade account.");
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Account Settings</h2>
        <p className="text-gray-600 mb-8">Manage your profile, preferences, and privacy settings securely.</p>

        <div className="flex gap-8 border-b border-gray-300 mb-8">
          <button
            onClick={() => setActiveTab("account")}
            className={`pb-4 px-1 text-lg font-medium transition ${
              activeTab === "account"
                ? "text-green-600 border-b-4 border-green-600"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Account
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`pb-4 px-1 text-lg font-medium transition ${
              activeTab === "password"
                ? "text-green-600 border-b-4 border-green-600"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Change Password
          </button>
          <button
            onClick={() => setActiveTab("kyc")}
            className={`pb-4 px-1 text-lg font-medium transition ${
              activeTab === "kyc"
                ? "text-green-600 border-b-4 border-green-600"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            KYC
          </button>
        </div>

        {activeTab === "account" && (
          <div className="bg-white rounded-lg shadow p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Account Details</h3>
            <p className="text-gray-600 mb-8">
              This is your public presence on the platform. Update your personal information here.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  value={form.email}
                  type="email"
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hospital Number</label>
                <input
                  name="hospital_number"
                  value={form.hospital_number}
                  onChange={handleChange}
                  type="text"
                  placeholder="Your Hospital Number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Name</label>
                <input
                  name="next_of_kin_name"
                  value={form.next_of_kin_name}
                  onChange={handleChange}
                  type="text"
                  placeholder="Next of Kin Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Phone Number</label>
                <input
                  name="next_of_kin_phone"
                  value={form.next_of_kin_phone}
                  onChange={handleChange}
                  type="tel"
                  placeholder="Next of Kin Phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Address</label>
                <input
                  name="next_of_kin_address"
                  value={form.next_of_kin_address}
                  onChange={handleChange}
                  type="text"
                  placeholder="Next of Kin Address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleSubmit}
                disabled={loading || !hasFormChanges()}
                className={`px-6 py-3 rounded-lg text-white font-medium ${
                  loading || !hasFormChanges()
                    ? "bg-purple-400 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => setIsCorporateModalOpen(true)}
                className="px-6 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700"
              >
                Upgrade to Corporate
              </button>
            </div>
          </div>
        )}

        {activeTab === "password" && (
          <div className="bg-white rounded-lg shadow p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Change Password</h3>
            <p className="text-gray-600 mb-8">
              Update your password. Ensure your new password is at least 8 characters long.
            </p>
            <div className="max-w-md space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <input
                  name="currentPassword"
                  value={form.currentPassword}
                  onChange={handleChange}
                  type="password"
                  placeholder="Enter Current Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  type="password"
                  placeholder="Enter New Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input
                  name="confirmNewPassword"
                  value={form.confirmNewPassword}
                  onChange={handleChange}
                  type="password"
                  placeholder="Enter New Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <button
                onClick={handlePasswordChange}
                disabled={loading}
                className="w-full py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
              >
                {loading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "kyc" && (
          <div className="bg-white rounded-lg shadow p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Account Details</h3>
                <p className="text-gray-600">
                  This is your public presence on the platform. Update your personal information here.
                </p>
              </div>
              {kycData && (
                <p className="text-lg font-medium text-red-600">KYC Status: Pending</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <select
                  name="account_type"
                  value={kycForm.account_type}
                  onChange={handleKycChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                >
                  <option>Individual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input
                  name="dob"
                  value={kycForm.dob}
                  onChange={handleKycChange}
                  type="text"
                  placeholder="dd/mm/yyyy"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  name="address"
                  value={kycForm.address}
                  onChange={handleKycChange}
                  type="text"
                  placeholder="Your address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Name</label>
                <input
                  name="next_of_kin_name"
                  value={kycForm.next_of_kin_name}
                  onChange={handleKycChange}
                  type="text"
                  placeholder="Next of Kin Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Phone Number</label>
                <input
                  name="next_of_kin_phone"
                  value={kycForm.next_of_kin_phone}
                  onChange={handleKycChange}
                  type="tel"
                  placeholder="Next of Kin Phone Number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Address</label>
                <input
                  name="next_of_kin_address"
                  value={kycForm.next_of_kin_address}
                  onChange={handleKycChange}
                  type="text"
                  placeholder="Next of Kin Address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                <input
                  name="id_number"
                  value={kycForm.id_number}
                  onChange={handleKycChange}
                  type="text"
                  placeholder="Your ID Number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID Type</label>
                <select
                  name="id_type"
                  value={kycForm.id_type}
                  onChange={handleKycChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                >
                  <option>NIN</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tier Level</label>
                <input
                  value="1"
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
            </div>
            <button
              onClick={handleKycSubmit}
              className="px-8 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
            >
              Update KYC
            </button>
          </div>
        )}

        <Dialog open={isCorporateModalOpen} onOpenChange={setIsCorporateModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upgrade to Corporate Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <p className="text-gray-600">
                Please upload the required documents to upgrade your account to corporate status.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1">Utility Bill</label>
                <input type="file" name="utility_bill" onChange={handleFileChange} className="w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CAC Certificate</label>
                <input type="file" name="cac_certificate" onChange={handleFileChange} className="w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Board Letter</label>
                <input type="file" name="board_letter" onChange={handleFileChange} className="w-full" />
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setIsCorporateModalOpen(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCorporateSubmit}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg"
                >
                  Submit Documents
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}